import React, { useState, useEffect, useMemo } from 'react';
import baseURL from '../../url';
import moneda from '../../moneda';
import * as XLSX from 'xlsx';
import { usePlan } from '../../PlanContext';
import './ReportePedidos.css';

export default function ReportePedidos() {
    const { isPlus } = usePlan();
    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState('hoy');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [fechaDia, setFechaDia] = useState('');

    useEffect(() => {
        fetch(`${baseURL}/pedidoGet.php`)
            .then(r => r.json())
            .then(data => { setPedidos(data.pedidos || []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const formatMoney = (n) => String(Math.round(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    const pedidosFiltrados = useMemo(() => {
        const ahora = new Date();
        return pedidos.filter(p => {
            if (!p.createdAt) return filtro === 'todos';
            const fecha = new Date(p.createdAt);
            switch (filtro) {
                case 'hoy': return fecha.toDateString() === ahora.toDateString();
                case 'semana': {
                    const hace7 = new Date(ahora); hace7.setDate(ahora.getDate() - 7);
                    return fecha >= hace7;
                }
                case 'mes': return fecha.getMonth() === ahora.getMonth() && fecha.getFullYear() === ahora.getFullYear();
                case 'dia': {
                    if (!fechaDia) return true;
                    const dia = new Date(fechaDia + 'T00:00:00');
                    return fecha.toDateString() === dia.toDateString();
                }
                case 'rango':
                    if (!fechaDesde || !fechaHasta) return true;
                    const desde = new Date(fechaDesde + 'T00:00:00');
                    const hasta = new Date(fechaHasta + 'T23:59:59');
                    return fecha >= desde && fecha <= hasta;
                case 'todos': return true;
                default: return true;
            }
        });
    }, [pedidos, filtro, fechaDesde, fechaHasta, fechaDia]);

    const stats = useMemo(() => {
        const total = pedidosFiltrados.length;
        const entregados = pedidosFiltrados.filter(p => p.estado === 'Entregado');
        const cancelados = pedidosFiltrados.filter(p => p.estado === 'Cancelado');
        const pendientes = pedidosFiltrados.filter(p => p.estado === 'Pendiente');
        const enPreparacion = pedidosFiltrados.filter(p => p.estado === 'En preparación');
        const listos = pedidosFiltrados.filter(p => p.estado === 'Listo');

        const ventasTotal = entregados.reduce((sum, p) => sum + Number(p.total || 0), 0);
        const ventasCanceladas = cancelados.reduce((sum, p) => sum + Number(p.total || 0), 0);
        const ticketPromedio = entregados.length > 0 ? ventasTotal / entregados.length : 0;

        // Pedidos web vs mesa
        const webPedidos = pedidosFiltrados.filter(p => !p.idMesa || p.idMesa === '0' || p.idMesa === 0);
        const mesaPedidos = pedidosFiltrados.filter(p => p.idMesa && p.idMesa !== '0' && p.idMesa !== 0);

        // Pago
        const pagoEfectivo = pedidosFiltrados.filter(p => p.pago === 'efectivo');
        const pagoTransferencia = pedidosFiltrados.filter(p => p.pago === 'transferencia');

        // Entrega
        const retiro = pedidosFiltrados.filter(p => p.entrega === 'Retiro personalmente' || p.entrega === 'Retiro en personalmente');
        const domicilio = pedidosFiltrados.filter(p => p.entrega && p.entrega !== 'Retiro personalmente' && p.entrega !== 'Retiro en personalmente');

        // Productos mas vendidos
        const productoConteo = {};
        entregados.forEach(p => {
            const prods = typeof p.productos === 'string' ? JSON.parse(p.productos) : p.productos;
            if (Array.isArray(prods)) {
                prods.forEach(prod => {
                    const key = prod.titulo;
                    if (!productoConteo[key]) productoConteo[key] = { titulo: key, cantidad: 0, ingresos: 0 };
                    productoConteo[key].cantidad += Number(prod.cantidad || 1);
                    productoConteo[key].ingresos += Number(prod.precio || 0) * Number(prod.cantidad || 1);
                });
            }
        });
        const topProductos = Object.values(productoConteo).sort((a, b) => b.cantidad - a.cantidad).slice(0, 10);

        // Ventas por dia
        const ventasPorDia = {};
        entregados.forEach(p => {
            if (!p.createdAt) return;
            const dia = new Date(p.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
            if (!ventasPorDia[dia]) ventasPorDia[dia] = { pedidos: 0, total: 0 };
            ventasPorDia[dia].pedidos++;
            ventasPorDia[dia].total += Number(p.total || 0);
        });

        // Horas pico
        const pedidosPorHora = {};
        pedidosFiltrados.forEach(p => {
            if (!p.createdAt) return;
            const hora = new Date(p.createdAt).getHours();
            pedidosPorHora[hora] = (pedidosPorHora[hora] || 0) + 1;
        });
        const horasPico = Object.entries(pedidosPorHora)
            .map(([h, c]) => ({ hora: `${h}:00`, cantidad: c }))
            .sort((a, b) => b.cantidad - a.cantidad)
            .slice(0, 5);

        // Estado de pago
        const pagados = pedidosFiltrados.filter(p => p.estadoPago === 'Pagado');
        const esperaPago = pedidosFiltrados.filter(p => p.estadoPago === 'Espera de pago');
        const confiable = pedidosFiltrados.filter(p => p.estadoPago === 'Cliente confiable');
        const sinContactar = pedidosFiltrados.filter(p => p.estadoPago === 'Sin contactar');

        return {
            total, entregados: entregados.length, cancelados: cancelados.length,
            pendientes: pendientes.length, enPreparacion: enPreparacion.length, listos: listos.length,
            ventasTotal, ventasCanceladas, ticketPromedio,
            webPedidos: webPedidos.length, mesaPedidos: mesaPedidos.length,
            pagoEfectivo: pagoEfectivo.length, pagoTransferencia: pagoTransferencia.length,
            retiro: retiro.length, domicilio: domicilio.length,
            topProductos, ventasPorDia, horasPico,
            pagados: pagados.length, esperaPago: esperaPago.length,
            confiable: confiable.length, sinContactar: sinContactar.length,
        };
    }, [pedidosFiltrados]);

    const tasaConversion = stats.total > 0 ? ((stats.entregados / stats.total) * 100).toFixed(1) : 0;
    const tasaCancelacion = stats.total > 0 ? ((stats.cancelados / stats.total) * 100).toFixed(1) : 0;

    const exportarExcel = () => {
        const wb = XLSX.utils.book_new();

        // Hoja 1: Pedidos filtrados (basic y plus)
        const datosPedidos = pedidosFiltrados.map(p => {
            const prods = typeof p.productos === 'string' ? JSON.parse(p.productos) : p.productos;
            const productosTexto = Array.isArray(prods) ? prods.map(pr => `${pr.cantidad}x ${pr.titulo}`).join(', ') : '';
            const base = {
                'ID Pedido': p.idPedido,
                'Fecha': p.createdAt ? new Date(p.createdAt).toLocaleString('es-ES') : '',
                'Cliente': p.nombre || '',
                'Telefono': p.telefono || '',
                'Productos': productosTexto,
                'Total': Number(p.total || 0),
                'Estado': p.estado || '',
                'Metodo Pago': p.pago || '',
                'Nota': p.nota || '',
            };
            if (!isPlus) return base;
            return {
                ...base,
                'Email': p.email || '',
                'Estado Pago': p.estadoPago || 'Sin asignar',
                'Entrega': p.entrega || '',
                'Canal': (!p.idMesa || p.idMesa === '0' || p.idMesa === 0) ? 'Web' : 'Mesa',
            };
        });
        const ws1 = XLSX.utils.json_to_sheet(datosPedidos);
        ws1['!cols'] = isPlus
            ? [{ wch: 10 }, { wch: 18 }, { wch: 20 }, { wch: 15 }, { wch: 40 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 8 }]
            : [{ wch: 10 }, { wch: 18 }, { wch: 20 }, { wch: 15 }, { wch: 40 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(wb, ws1, 'Pedidos');

        // Hoja 2: Resumen (basic = básico, plus = completo)
        const resumenBasico = [
            { 'Metrica': 'Total pedidos', 'Valor': stats.total },
            { 'Metrica': 'Pendientes', 'Valor': stats.pendientes },
            { 'Metrica': 'En preparacion', 'Valor': stats.enPreparacion },
            { 'Metrica': 'Listos', 'Valor': stats.listos },
            { 'Metrica': 'Entregados', 'Valor': stats.entregados },
            { 'Metrica': 'Cancelados', 'Valor': stats.cancelados },
            { 'Metrica': '', 'Valor': '' },
            { 'Metrica': 'Pago efectivo', 'Valor': stats.pagoEfectivo },
            { 'Metrica': 'Pago transferencia', 'Valor': stats.pagoTransferencia },
        ];
        const resumenPlus = [
            ...resumenBasico,
            { 'Metrica': '', 'Valor': '' },
            { 'Metrica': `Ventas totales (${moneda})`, 'Valor': Math.round(stats.ventasTotal) },
            { 'Metrica': `Ticket promedio (${moneda})`, 'Valor': Math.round(stats.ticketPromedio) },
            { 'Metrica': 'Tasa de conversion (%)', 'Valor': tasaConversion },
            { 'Metrica': 'Tasa de cancelacion (%)', 'Valor': tasaCancelacion },
            { 'Metrica': `Ventas perdidas (${moneda})`, 'Valor': Math.round(stats.ventasCanceladas) },
            { 'Metrica': '', 'Valor': '' },
            { 'Metrica': 'Canal Web', 'Valor': stats.webPedidos },
            { 'Metrica': 'Canal Mesa', 'Valor': stats.mesaPedidos },
            { 'Metrica': 'Entrega domicilio', 'Valor': stats.domicilio },
            { 'Metrica': 'Retiro en tienda', 'Valor': stats.retiro },
        ];
        const ws2 = XLSX.utils.json_to_sheet(isPlus ? resumenPlus : resumenBasico);
        ws2['!cols'] = [{ wch: 30 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Resumen');

        if (isPlus) {
            // Hoja 3: Top productos
            if (stats.topProductos.length > 0) {
                const ws3 = XLSX.utils.json_to_sheet(stats.topProductos.map((p, i) => ({
                    '#': i + 1,
                    'Producto': p.titulo,
                    'Cantidad vendida': p.cantidad,
                    [`Ingresos (${moneda})`]: Math.round(p.ingresos),
                })));
                ws3['!cols'] = [{ wch: 5 }, { wch: 30 }, { wch: 18 }, { wch: 18 }];
                XLSX.utils.book_append_sheet(wb, ws3, 'Top Productos');
            }

            // Hoja 4: Ventas por dia
            const diasData = Object.entries(stats.ventasPorDia);
            if (diasData.length > 0) {
                const ws4 = XLSX.utils.json_to_sheet(diasData.map(([dia, d]) => ({
                    'Dia': dia,
                    'Pedidos': d.pedidos,
                    [`Total (${moneda})`]: Math.round(d.total),
                })));
                ws4['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 15 }];
                XLSX.utils.book_append_sheet(wb, ws4, 'Ventas por Dia');
            }
        }

        const filtroNombre = filtro === 'rango' ? `${fechaDesde}_${fechaHasta}` : filtro === 'dia' ? fechaDia : filtro;
        XLSX.writeFile(wb, `Reporte_Pedidos_${filtroNombre}.xlsx`);
    };

    if (loading) return <div className='rpLoading'>Cargando reportes...</div>;

    return (
        <div className='rpContainer'>
            <div className='rpTituloRow'>
                <h3 className='rpTitulo'>Reportes y Metricas</h3>
                <button className='rpExportarBtn' onClick={exportarExcel}>Exportar Excel</button>
            </div>

            {/* Filtros */}
            <div className='rpFiltros'>
                {(isPlus ? ['hoy', 'semana', 'mes', 'todos', 'rango'] : ['hoy', 'semana', 'mes', 'dia']).map(f => (
                    <button key={f} className={`rpFiltroBtn ${filtro === f ? 'rpFiltroBtnActivo' : ''}`} onClick={() => setFiltro(f)}>
                        {f === 'hoy' ? 'Hoy' : f === 'semana' ? 'Semana' : f === 'mes' ? 'Este mes' : f === 'todos' ? 'Todos' : f === 'dia' ? 'Dia especifico' : 'Rango'}
                    </button>
                ))}
                {filtro === 'rango' && isPlus && (
                    <div className='rpRangoFechas'>
                        <input type='date' value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                        <span>a</span>
                        <input type='date' value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                    </div>
                )}
                {filtro === 'dia' && !isPlus && (
                    <div className='rpRangoFechas'>
                        <input type='date' value={fechaDia} onChange={e => setFechaDia(e.target.value)} />
                    </div>
                )}
            </div>

            {/* KPIs principales */}
            {isPlus && <div className='rpKpis'>
                <div className='rpKpiCard rpKpiVentas'>
                    <span className='rpKpiLabel'>Ventas totales</span>
                    <span className='rpKpiValor'>{moneda} {formatMoney(stats.ventasTotal)}</span>
                    <span className='rpKpiSub'>{stats.entregados} pedidos entregados</span>
                </div>
                <div className='rpKpiCard rpKpiTicket'>
                    <span className='rpKpiLabel'>Ticket promedio</span>
                    <span className='rpKpiValor'>{moneda} {formatMoney(stats.ticketPromedio)}</span>
                </div>
                <div className='rpKpiCard rpKpiConversion'>
                    <span className='rpKpiLabel'>Tasa de conversion</span>
                    <span className='rpKpiValor'>{tasaConversion}%</span>
                    <span className='rpKpiSub'>{stats.entregados} de {stats.total} pedidos</span>
                </div>
                <div className='rpKpiCard rpKpiCancel'>
                    <span className='rpKpiLabel'>Tasa de cancelacion</span>
                    <span className='rpKpiValor'>{tasaCancelacion}%</span>
                    <span className='rpKpiSub'>{stats.cancelados} cancelados</span>
                </div>
            </div>}

            {/* Estado de pedidos */}
            <div className='rpSeccion'>
                <h4>Estado de pedidos</h4>
                <div className='rpEstadosGrid'>
                    <div className='rpEstadoItem rpEstPendiente'><span>{stats.pendientes}</span><small>Pendientes</small></div>
                    <div className='rpEstadoItem rpEstPreparacion'><span>{stats.enPreparacion}</span><small>En preparacion</small></div>
                    <div className='rpEstadoItem rpEstListo'><span>{stats.listos}</span><small>Listos</small></div>
                    <div className='rpEstadoItem rpEstEntregado'><span>{stats.entregados}</span><small>Entregados</small></div>
                    <div className='rpEstadoItem rpEstCancelado'><span>{stats.cancelados}</span><small>Cancelados</small></div>
                </div>
            </div>

            {isPlus ? (
                <div className='rpDosColumnas'>
                    {/* Canal de venta */}
                    <div className='rpSeccion'>
                        <h4>Canal de venta</h4>
                        <div className='rpBarras'>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Web</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraWeb' style={{ width: stats.total > 0 ? `${(stats.webPedidos / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.webPedidos}</span>
                            </div>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Mesa</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraMesa' style={{ width: stats.total > 0 ? `${(stats.mesaPedidos / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.mesaPedidos}</span>
                            </div>
                        </div>
                    </div>

                    {/* Metodo de pago */}
                    <div className='rpSeccion'>
                        <h4>Metodo de pago</h4>
                        <div className='rpBarras'>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Efectivo</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraEfectivo' style={{ width: stats.total > 0 ? `${(stats.pagoEfectivo / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.pagoEfectivo}</span>
                            </div>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Transferencia</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraTransf' style={{ width: stats.total > 0 ? `${(stats.pagoTransferencia / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.pagoTransferencia}</span>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className='rpSeccion'>
                    <h4>Metodo de pago</h4>
                    <div className='rpBarras'>
                        <div className='rpBarra'>
                            <span className='rpBarraLabel'>Efectivo</span>
                            <div className='rpBarraTrack'>
                                <div className='rpBarraFill rpBarraEfectivo' style={{ width: stats.total > 0 ? `${(stats.pagoEfectivo / stats.total) * 100}%` : '0%' }}></div>
                            </div>
                            <span className='rpBarraNum'>{stats.pagoEfectivo}</span>
                        </div>
                        <div className='rpBarra'>
                            <span className='rpBarraLabel'>Transferencia</span>
                            <div className='rpBarraTrack'>
                                <div className='rpBarraFill rpBarraTransf' style={{ width: stats.total > 0 ? `${(stats.pagoTransferencia / stats.total) * 100}%` : '0%' }}></div>
                            </div>
                            <span className='rpBarraNum'>{stats.pagoTransferencia}</span>
                        </div>
                    </div>
                </div>
            )}

            {isPlus && <>
                <div className='rpDosColumnas'>
                    {/* Tipo de entrega */}
                    <div className='rpSeccion'>
                        <h4>Tipo de entrega</h4>
                        <div className='rpBarras'>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Domicilio</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraDomicilio' style={{ width: stats.total > 0 ? `${(stats.domicilio / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.domicilio}</span>
                            </div>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Retiro</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraRetiro' style={{ width: stats.total > 0 ? `${(stats.retiro / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.retiro}</span>
                            </div>
                        </div>
                    </div>

                    {/* Estado de pago */}
                    <div className='rpSeccion'>
                        <h4>Estado de pago</h4>
                        <div className='rpBarras'>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Pagado</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraPagado' style={{ width: stats.total > 0 ? `${(stats.pagados / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.pagados}</span>
                            </div>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Espera</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraEspera' style={{ width: stats.total > 0 ? `${(stats.esperaPago / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.esperaPago}</span>
                            </div>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Confiable</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraConfiable' style={{ width: stats.total > 0 ? `${(stats.confiable / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.confiable}</span>
                            </div>
                            <div className='rpBarra'>
                                <span className='rpBarraLabel'>Sin contactar</span>
                                <div className='rpBarraTrack'>
                                    <div className='rpBarraFill rpBarraSinContactar' style={{ width: stats.total > 0 ? `${(stats.sinContactar / stats.total) * 100}%` : '0%' }}></div>
                                </div>
                                <span className='rpBarraNum'>{stats.sinContactar}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Productos mas vendidos */}
                <div className='rpSeccion'>
                    <h4>Top 10 productos mas vendidos</h4>
                    {stats.topProductos.length === 0 ? (
                        <p className='rpVacio'>Sin datos de productos vendidos</p>
                    ) : (
                        <table className='rpTabla'>
                            <thead>
                                <tr><th>#</th><th>Producto</th><th>Vendidos</th><th>Ingresos</th></tr>
                            </thead>
                            <tbody>
                                {stats.topProductos.map((prod, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td>{prod.titulo}</td>
                                        <td>{prod.cantidad}</td>
                                        <td>{moneda} {formatMoney(prod.ingresos)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Horas pico */}
                <div className='rpSeccion'>
                    <h4>Horas con mas pedidos</h4>
                    {stats.horasPico.length === 0 ? (
                        <p className='rpVacio'>Sin datos</p>
                    ) : (
                        <div className='rpHorasPico'>
                            {stats.horasPico.map((h, i) => (
                                <div key={i} className='rpHoraItem'>
                                    <span className='rpHora'>{h.hora}</span>
                                    <div className='rpHoraBarTrack'>
                                        <div className='rpHoraBarFill' style={{ width: `${(h.cantidad / stats.horasPico[0].cantidad) * 100}%` }}></div>
                                    </div>
                                    <span className='rpHoraNum'>{h.cantidad}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Ventas por dia */}
                <div className='rpSeccion'>
                    <h4>Ventas por dia</h4>
                    {Object.keys(stats.ventasPorDia).length === 0 ? (
                        <p className='rpVacio'>Sin ventas en el periodo</p>
                    ) : (
                        <table className='rpTabla'>
                            <thead>
                                <tr><th>Dia</th><th>Pedidos</th><th>Total</th></tr>
                            </thead>
                            <tbody>
                                {Object.entries(stats.ventasPorDia).map(([dia, data], i) => (
                                    <tr key={i}>
                                        <td>{dia}</td>
                                        <td>{data.pedidos}</td>
                                        <td>{moneda} {formatMoney(data.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Perdidas por cancelacion */}
                {stats.ventasCanceladas > 0 && (
                    <div className='rpSeccion rpSeccionAlerta'>
                        <h4>Ventas perdidas por cancelacion</h4>
                        <p className='rpPerdida'>{moneda} {formatMoney(stats.ventasCanceladas)} en {stats.cancelados} pedidos cancelados</p>
                    </div>
                )}
            </>}
        </div>
    );
}
